import React, { useState, useEffect } from 'react'
import { Save, Eye, EyeOff, Edit } from 'lucide-react'
import { adminAPI } from '../../services/api'
import toast from 'react-hot-toast'

interface EmailConfig {
  smtp_host: string
  smtp_port: number
  smtp_secure: 'ssl' | 'tls'
  smtp_user: string
  smtp_pass: string
  from_email: string
  from_name: string
}

const EmailSettings: React.FC = () => {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [config, setConfig] = useState<EmailConfig>({
    smtp_host: '',
    smtp_port: 465,
    smtp_secure: 'ssl',
    smtp_user: '',
    smtp_pass: '',
    from_email: '',
    from_name: ''
  })

  useEffect(() => {
    fetchEmailConfig()
  }, [])

  const fetchEmailConfig = async () => {
    try {
      const response = await adminAPI.getEmailConfig()
      console.log('Email config response:', response)
      // Sprawdzamy czy dane są w response.data czy response.data.data
      const configData = response.data.data || response.data
      setConfig(configData)
      setIsEditing(false)
    } catch (error) {
      console.error('Error fetching email config:', error)
      // Jeśli brak danych, włączamy edycję
      setIsEditing(true)
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    
    try {
      await adminAPI.updateEmailConfig(config)
      toast.success('Konfiguracja email zapisana')
      setIsEditing(false)
    } catch (error) {
      console.error('Error saving email config:', error)
      toast.error('Błąd zapisu konfiguracji')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return <div className="text-white">Ładowanie...</div>
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-white mb-2">Ustawienia Poczty Email</h1>
        <p className="text-gray-400">Konfiguracja serwera SMTP do wysyłania wiadomości</p>
      </div>

      <form onSubmit={handleSubmit} className="bg-dark-800 rounded-lg p-6 border border-dark-700">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Host SMTP
            </label>
            <input
              type="text"
              value={config.smtp_host}
              onChange={(e) => setConfig({...config, smtp_host: e.target.value})}
              className="w-full px-3 py-2 bg-dark-700 border border-dark-500 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-primary"
              disabled={!isEditing}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Port SMTP
            </label>
            <input
              type="number"
              value={config.smtp_port}
              onChange={(e) => setConfig({...config, smtp_port: parseInt(e.target.value)})}
              className="w-full px-3 py-2 bg-dark-700 border border-dark-500 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-primary"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Zabezpieczenie
            </label>
            <select
              value={config.smtp_secure}
              onChange={(e) => setConfig({...config, smtp_secure: e.target.value as 'ssl' | 'tls'})}
              className="w-full px-3 py-2 bg-dark-700 border border-dark-500 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-primary"
              disabled={!isEditing}
            >
              <option value="ssl">SSL</option>
              <option value="tls">TLS</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Użytkownik SMTP (email)
            </label>
            <input
              type="email"
              value={config.smtp_user}
              onChange={(e) => setConfig({...config, smtp_user: e.target.value})}
              className="w-full px-3 py-2 bg-dark-700 border border-dark-500 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-primary"
              required
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Hasło SMTP
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={config.smtp_pass}
                onChange={(e) => setConfig({...config, smtp_pass: e.target.value})}
                className="w-full px-3 py-2 bg-dark-700 border border-dark-500 rounded-md text-white pr-10 focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Wprowadź nowe hasło aby zmienić"
                disabled={!isEditing}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
              >
                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Email nadawcy
            </label>
            <input
              type="email"
              value={config.from_email}
              onChange={(e) => setConfig({...config, from_email: e.target.value})}
              className="w-full px-3 py-2 bg-dark-700 border border-dark-500 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-primary"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Nazwa nadawcy
            </label>
            <input
              type="text"
              value={config.from_name}
              onChange={(e) => setConfig({...config, from_name: e.target.value})}
              className="w-full px-3 py-2 bg-dark-700 border border-dark-500 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-primary"
              required
            />
          </div>
        </div>

        <div className="mt-6 flex justify-end space-x-4">
          {!isEditing ? (
            <button
              type="button"
              onClick={() => setIsEditing(true)}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              <Edit className="h-4 w-4" />
              <span>Edytuj</span>
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={() => {
                  fetchEmailConfig()
                  setIsEditing(false)
                }}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-500"
              >
                Anuluj
              </button>
              <button
                type="submit"
                disabled={saving}
                className="flex items-center space-x-2 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 disabled:opacity-50"
              >
                <Save className="h-4 w-4" />
                <span>{saving ? 'Zapisywanie...' : 'Zapisz'}</span>
              </button>
            </>
          )}
        </div>
      </form>
    </div>
  )
}

export default EmailSettings